// BRK-S278 — the router state the wizard hands the submission detail page at
// the moment of filing (design § 5.1). BRK-S184 adds the won-bid screen's
// variant: the same page, the same banner, a different sentence.
//
// ROUTER STATE, NEVER THE QUERY STRING. The banner it drives is about the
// moment of filing, so a shared or bookmarked `/surety/submissions/{id}` must
// never show it — and router state is exactly what a copied URL leaves behind.
// The detail page clears it on dismiss (`replace`, `state: null`) so a reload
// does not repeat the banner either.

/** What the wizard navigates with on a submit (and on a 409 already-filed). */
export interface SubmittedState {
  submitted: true;
  /** The filing was referred — the banner then says so. */
  referred: boolean;
}

/**
 * What the won-bid screen navigates with after its PUT answered 2xx
 * (BRK-S184). `status` is copied from THAT response — the v2 status the
 * server moved the order to (`pp_requested`, or `underwriter_review` over a
 * limit; `workflowStatus` until it left the contract, BRK-S511) — so the
 * banner never states one the server did not (NN #7). Null when the response
 * carried none.
 */
export interface WonSubmittedState {
  wonSubmitted: true;
  status: string | null;
  /** The bonds the banner names (BRK-S327): the response's recorded value,
   *  else what the 2xx accepted. Read as `unknown`-narrowed text. */
  bondsRequested: string | null;
}

/**
 * Read `location.state` as a `SubmittedState`, or `null`.
 *
 * Narrowed from `unknown` (NN #7): history state survives reloads and can be
 * anything another page, an older build or an extension left there, so only a
 * literal `submitted: true` counts.
 */
export function readSubmittedState(state: unknown): SubmittedState | null {
  if (typeof state !== "object" || state === null) return null;
  if (!("submitted" in state) || state.submitted !== true) return null;
  const referred = "referred" in state && state.referred === true;
  return { submitted: true, referred };
}

/** Read `location.state` as a `WonSubmittedState`, or `null` — the same
 *  narrowing: only a literal `wonSubmitted: true` counts. */
export function readWonSubmittedState(
  state: unknown,
): WonSubmittedState | null {
  if (typeof state !== "object" || state === null) return null;
  if (!("wonSubmitted" in state) || state.wonSubmitted !== true) return null;
  const stage =
    "status" in state && typeof state.status === "string" ? state.status : null;
  const bonds =
    "bondsRequested" in state && typeof state.bondsRequested === "string"
      ? state.bondsRequested
      : null;
  return { wonSubmitted: true, status: stage, bondsRequested: bonds };
}
